"use client";

import { useEffect, useMemo, useState } from "react";
import { Braces } from "lucide-react";

import CuoButton from "@/app/admin/_common/CuoButton";
import { Input } from "@/app/admin/_common/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import EmailBuilderEditor from "@/app/admin/_common/email-builder/EmailBuilderEditor";
import {
  getEditorDocument,
  resetDocument,
  useDocument,
} from "@/app/admin/_common/email-builder/documents/editor/EditorContext";
import { renderToStaticMarkup } from "@/app/admin/_common/email-builder/documents/reader/CuoEmailReader";
import { patchDocumentHeaderNavBorders } from "@/app/admin/_common/email-builder/patchHeaderNavBorder";
import { resolveInitialDocument } from "@/app/admin/_common/email-builder/resolveInitialDocument";
import type {
  AutomationTemplate,
  ContextField,
} from "@/app/_services/AutomationService";
import { toast } from "@/app/_utils/toast";

import VariableList from "./VariableList";

export default function EmailTemplateBuilder({
  automationId,
  template,
  contextFields,
  legacyFields,
  onSave,
}: {
  automationId: string;
  template: AutomationTemplate;
  contextFields: ContextField[];
  legacyFields: { path: string; label: string }[];
  onSave: (body: {
    key: string;
    subject?: string;
    body?: string;
    body_json?: any;
  }) => Promise<boolean>;
}) {
  // The editor store is a module-level singleton, so switching templates has to remount
  // and reset it -- otherwise the previous template's blocks stay on screen.
  const editorKey = `${automationId}:${template.key}`;
  const loaded = { template: template.body, template_json: template.body_json ?? null };

  const initialDocument = useMemo(() => resolveInitialDocument(loaded), [editorKey]);

  const [subject, setSubject] = useState(template.subject ?? "");
  const [initialSubject, setInitialSubject] = useState(template.subject ?? "");
  const [docSnapshot, setDocSnapshot] = useState(JSON.stringify(initialDocument));
  const [isSaving, setIsSaving] = useState(false);

  const document = useDocument();
  const isDirty =
    subject !== initialSubject || JSON.stringify(document) !== docSnapshot;

  useEffect(() => {
    resetDocument(initialDocument);
    setDocSnapshot(JSON.stringify(initialDocument));
    setSubject(template.subject ?? "");
    setInitialSubject(template.subject ?? "");
  }, [editorKey]);

  async function handleSave() {
    setIsSaving(true);
    try {
      const doc = patchDocumentHeaderNavBorders(getEditorDocument());
      const html = renderToStaticMarkup(doc, { rootBlockId: "root" });
      const ok = await onSave({
        key: template.key,
        subject,
        body: html,
        body_json: doc,
      });
      if (ok) {
        setDocSnapshot(JSON.stringify(doc));
        setInitialSubject(subject);
      }
    } finally {
      setIsSaving(false);
    }
  }

  function handleDiscard() {
    resetDocument(initialDocument);
    setDocSnapshot(JSON.stringify(initialDocument));
    setSubject(initialSubject);
  }

  async function copyVariable(path: string) {
    const token = `{{${path}}}`;
    await navigator.clipboard.writeText(token);
    toast.success(`Copied ${token}`);
  }

  const headerSlot = (
    <div className="flex items-center gap-2">
      <Input
        placeholder="Subject line"
        value={subject}
        className="max-w-none flex-1"
        onChange={(e) => setSubject(e.target.value)}
      />
      {contextFields.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <CuoButton
              variant="outline"
              className="shrink-0 whitespace-nowrap"
              icon={<Braces className="size-4" />}
              label="Variables"
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="max-h-[420px] w-[380px] overflow-y-auto"
          >
            <DropdownMenuLabel className="text-[11.5px] font-normal text-muted-foreground">
              Click to copy, then paste into a block
            </DropdownMenuLabel>
            <div className="px-2 pb-2">
              <VariableList
                contextFields={contextFields}
                legacyFields={legacyFields}
                onPick={copyVariable}
              />
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col">
        <EmailBuilderEditor key={editorKey} headerSlot={headerSlot} />
      </div>

      {isDirty && (
        <div className="flex flex-shrink-0 items-center justify-between gap-3 border-t border-border bg-card px-5 py-3">
          <span className="text-[12.5px] text-muted-foreground">
            You have unsaved changes
          </span>
          <div className="flex items-center gap-2">
            <CuoButton variant="outline" label="Discard" onClick={handleDiscard} />
            <CuoButton label="Save" disableOnAsync={isSaving} onClick={handleSave} />
          </div>
        </div>
      )}
    </div>
  );
}
